import Store from 'store/store';
import cached from 'utils/cached';
import * as categories from 'utils/gdpr/categories';
import runHTMLScripts from 'utils/runHTMLScripts';
import { escapeObjectValues } from 'utils/utils';

const isProd = !__IS_LOCAL__ && !__IS_DEMO__;
const showLogs = typeof window !== 'undefined' && /[?&]powdebug([&=]|$)/i.test(location.search);

function log(...args) {
	if (!showLogs) {
		return;
	}
	// Note: logs will not show up on prod because of the drop_console terser option in webpack
	// Is there a way to bypass that for this line specifically ?
	console.log('[PowDebug]', ...args);
}
function logPixel(icon, message, pixel, code) {
	log(...[
		icon,
		pixel.static && 'static', pixel.type, 'pixel',
		pixel.id && '[' + pixel.id + ']',
		pixel.category && '(' + pixel.category + ')',
		message,
		code && '\nExecuted Code:',
		// Converting code to string hides things in the case of bound functions, but passing it to console.log shows the source
		code
	].filter(e => e));
}

let _init;
let _getStaticPixels;
let _register;
let _fire;
let _getVariables;
if (USE_TRACKING_V2) {
	const pixels = {};
	const queues = {};

	const shouldUsePixel = pixel => pixel?.code && !(pixel.prodOnly && (__IS_LOCAL__ || __IS_DEMO__));
	const isHTML = code => /^\s*</.test(code);
	const safeStringForHTMLScript = code => code.replace(/<\/script>/ig, '<\\/script>').replace(/<!--/ig, '<\\!--');

	const register = (pixel, id) => {
		if (!shouldUsePixel(pixel) || !pixel.trigger || pixel.static) {
			return;
		}
		if (!pixel.id && id) {
			pixel.id = id;
		}
		let trigger = pixel.trigger;
		if (trigger === 'pageView' && pixel.page) {
			trigger += ':' + pixel.page;
		}
		if (!pixels[trigger]) {
			pixels[trigger] = [];
		}
		pixels[trigger].push(pixel);
	};
	const addToQueue = (category, code) => {
		if (!queues[category]) {
			queues[category] = [];
		}
		queues[category].push(code);
	};

	const withVariables = (code, variables) => {
		if (!variables) {
			return code;
		}
		if (typeof code === 'function') {
			return code.bind(null, variables);
		}
		return code?.replace?.(/(\\)?\{\{(.+?)\}\}/g, (match, escaped, variable) => {
			if (escaped) {
				return '{{' + variable + '}}';
			}
			let raw = false;
			if (variable[0] === '=') {
				raw = true;
				variable = variable.slice(1);
			}
			// TODO: add way to specify default value (e.g. {{variable:default}})
			if (!Object.prototype.hasOwnProperty.call(variables, variable)) {
				return match;
			}
			if (raw || typeof variables[variable] !== 'string') {
				return variables[variable];
			}
			return safeStringForHTMLScript(
				JSON.stringify(variables[variable])
					.slice(1, -1)
					.replace(/\\"/g, '"')
					.replace(/'/g, '\\\'')
			);
		}) || '';
	};

	const runCode = async (code) => {
		try {
			if (typeof code === 'function') {
				await code();
				return;
			}
			// Handle HTML tags vs simple JS
			// Should this be checked in runHTMLScripts directly?
			if (isHTML(code)) {
				await runHTMLScripts(code);
			} else {
				// (0, eval) to run the code in the global context
				//   and make sure they cannot access/modify stuff like the "queues" variable
				(0, eval)(code);
			}
		} catch (err) {
			console.error(err);
		}
	};

	// Inserts the pixel code to the page
	const fire = async (type, variables = {}) => {
		if (!pixels[type]) {
			// log(`no pixels fired for event: ${type}`);
			return;
		}
		log(`--- FIRING PIXELS FOR EVENT: ${type} ---`);
		for (let pixel of pixels[type]) {
			if (pixel.once && pixel.triggered) {
				return;
			}
			pixel.triggered = (pixel.triggered || 0) + 1;
			let code = withVariables(pixel.code, variables);
			if (!pixel.category || categories.has(pixel.category)) {
				logPixel('✔️⚡', 'fired', pixel, code);
				await runCode(code);
			} else {
				logPixel('🛑🍪', `stopped from firing. Pixel Category: ${pixel.category} not within accepted categories (${categories.toString()})${pixel.useQueue ? '. The pixel was queued and will fire if the desired category is accepted later' : ''}`, pixel);
				if (pixel.useQueue) {
					addToQueue(pixel.category, [pixel, code]);
				}
			}
		}
	};

	categories.addChangeListener(cats => {
		log(`🍪📃 Accepted cookie categories changed. New accepted categories: ${cats.join(',')}`);
		cats.forEach(async category => {
			if (!queues[category]) {
				return;
			}
			for (let [pixel, code] of queues[category]) {
				logPixel('✔️⚡', 'fired from queue', pixel, code);
				await runCode(code);
			}
			queues[category].length = 0;
		});
	});

	// Cache the variables that will never change over time
	const getStaticBaseVariables = cached(() => {
		let appData = Store.get().appData;
		const debug_mode = !isProd;
		const movieTitle = appData?.meta?.title?.locale;
		const app = movieTitle + ' - ' + __REGION__.toUpperCase();
		return {
			movieTitle,
			app,
			movie: __TITLE__,
			studioName: appData?.meta?.studio?.name,
			studio: __STUDIO__,
			region: __REGION__,
			releaseDate: appData?.meta?.dates?.release,
			boxOfficeDate: appData?.meta?.dates?.boxOffice,
			vanityUrl: appData?.meta?.url,
			domain: typeof window !== 'undefined' ? location.host : '',
			debug_mode,
		};
	});
	let customVariables = {};
	const getVariables = (eventSpecific) => {
		const store = Store.get();
		const activePage = store.activePage;
		let customExpanded = Object.fromEntries(Object.entries(customVariables).map(([name, value]) => {
			if (typeof value === 'string') {
				if (value[0] === '@') {
					value = value.slice(1).split('.').reduce((r, p) => r?.[p], store);
				} else if (value[0] === '=') {
					value = value.slice(1);
				}
			}
			return [name, value];
		}));
		const result = {
			...getStaticBaseVariables(),
			page: activePage,
			pageName: getPageName(activePage),
			...customExpanded,
			...eventSpecific,
		};
		return escapeObjectValues(result);
	};

	const getPageName = (pageId) => {
		const appData = Store.get().appData;
		let nameValue = appData?.pages?.[pageId]?.name;
		if (nameValue) {
			return appData.copy?.baseCopy?.[nameValue] || nameValue;
		}
		// boltHomeEnt -> homeEnt
		const pageWithoutBolt = pageId.replace(/^bolt([A-Z])/, (m, l) => l.toLowerCase());
		// homeEnt -> Home Ent
		return pageWithoutBolt.replace(/^([a-z])|([a-z])([A-Z])/g, (m, first, wordEnd, nextWord) => first?.toUpperCase() || `${wordEnd} ${nextWord}`);
	};

	// -- TRIGGERS --
	const firePageView = (page) => {
		let variables = getVariables({ page, pageName: getPageName(page) });
		fire('pageView', variables);
		fire('pageView:' + page, variables);
	};

	Store.on('LANDING', ({ page }) => {
		const loadTime = Math.round(window.startLoadTime);
		let variables = getVariables({ page, pageName: getPageName(page), loadTime });
		fire('landing', variables);
		firePageView(page);
		fire('timingComplete', variables);
	});
	Store.on('ACTIVE_PAGE_UPDATED', firePageView);
	Store.on('TICKET_CONVERSION', ({ date, time, format, provider, theater, source, conversion, day, url, movieTitle, movieSlug, ticketType = 'tickets' }) => (
		fire('ticket', getVariables({
			theater: theater?.name,
			chain: theater?.chain?.name,
			screeningDate: date,
			screeningTime: time,
			format,
			ticketProvider: provider,
			source,
			conversion,
			day,
			url,
			screeningMovie: movieSlug,
			screeningMovieTitle: movieTitle,
			ticketType
		}))
	));

	/** Home Ent Events */
	Store.on('HOME_ENT_CONVERSION', ({ product, merchant, selectedFormat, region, availability, dataProvider }) => (
		fire('homeEntLinkOut', getVariables({
			merchant,
			region,
			availability,
			format: selectedFormat,
			edition: product.edition,
			price: product.price,
			dataProvider
		}))
	));
	Store.on('HOME_ENT_MERCHANT_SELECT', ({ merchant, region }) => fire('homeEntMerchantSelection', getVariables({ merchant, region })));
	Store.on('HOME_ENT_FORMAT_SELECT', (format) => fire('homeEntFormatSelection', getVariables({ format })));
	Store.on('HOME_ENT_PROVIDER_ATTRIBUTION_CLICK', (provider) => fire('homeEntProviderClick', getVariables({ provider })));

	/** Social Events */
	Store.on('CLICKED_SHARE', ({ label }) => fire('socialShare', getVariables({ socialSharePlatform: label })));
	Store.on('CLICKED_FOLLOW', (label) => fire('socialFollow', getVariables({ socialFollowPlatform: label })));

	/* Synopsis Events */
	Store.on('SYNOPSIS_ENLARGE_IMAGE', (imageName) => fire('synopsisImageEnlarge', getVariables({ synopsisImage: imageName })));
	Store.on('SYNOPSIS_DOWNLOAD_IMAGE', (imageName) => fire('synopsisImageDownload', getVariables({ synopsisImage: imageName })));

	/* Gallery Events */
	Store.on('GALLERY_VIEW_IMAGE', (activeImage) => fire('galleryImageView', getVariables({ galleryImage: activeImage })));
	Store.on('GALLERY_DOWNLOAD_IMAGE', (activeImage) => fire('galleryImageDownload', getVariables({ galleryImage: activeImage })));
	Store.on('GALLERY_DOWNLOAD_ALL', () => fire('galleryImageDownloadAll', getVariables()));

	/* Showtimes / Tickets Maps Events */
	Store.on('MAP_REQUEST_DIRECTIONS', (travelMode) => fire('showtimeMapRequestDirections', getVariables({ travelMode: travelMode.toLowerCase() })));

	/* Region Selector Events */
	Store.on('REGION_SELECTOR_CLICKED', (origin, destination) => fire('regionSelectorClick', getVariables({ origin, destination })));

	/* Video Events */

	/* Tout Events */
	Store.on('TOUT_CLICK', (toutType, _, toutLabel) => fire('toutSelectionClick', getVariables({ toutType, toutLabel })));

	/* Expandable Text List */
	Store.on('TEXT_LIST_CLICK', name => fire('textListClick', getVariables({ name })));

	/* Notification Banner */
	Store.on('NOTIFICATION_BANNER_CLICK', (page, _, label) => fire('notificationBannerClick', getVariables({ page, label })));

	/* Landing Page Events */
	Store.on('MOVIE_DETAILS_BUTTON_CLICK', (page, label) => fire('movieDetailsButtonClick', getVariables({ page, label })));
	Store.on('HEADER_PERSISTENT_BUTTON_CLICK', (action, category, label) => fire('headerPersistentButtonClick', getVariables({ label })));
	Store.on('SHOW_CAL_REMIND', () => fire('showCalendarReminder', getVariables()));
	Store.on('TOGGLE_SHARE_OPEN', () => fire('openShareDialog', getVariables()));

	Store.on('SELECT_TIME', ({ screening, theater, ticketType = 'tickets'}) => {
		const { date, time, format } = screening;
		const { name, chain } = theater;
		const chainName = chain?.name;
		fire('showtimeSelectTime', getVariables({ name, chainName, date, time, format, ticketType }));
	});

	Store.on('SELECT_THEATER', (theater, _, ticketType = 'tickets') => {
		const name = theater.name || '';
		const chain = theater?.chain?.name || '';
		const theaterChainName = name + '-' + chain;
		fire('showtimeSelectTheater', getVariables({ theaterChainName, ticketType }));
	});

	Store.on('GROUP_BOOKINGS_INQUIRE', theater => {
		const name = theater.name || '';
		const chain = theater?.chain?.name || '';
		const theaterChainName = name + '-' + chain;
		fire('groupBookingsInquire', getVariables({ theaterChainName }));
	});

	Store.on('GROUP_BOOKINGS_NAVIGATE_STAGES', direction => {
		fire('groupBookingsNavigateStages', getVariables({ direction }));
	});

	Store.on('GROUP_BOOKINGS_SUBMIT', theater => {
		const name = theater.name || '';
		const chain = theater?.chain?.name || '';
		const theaterChainName = name + '-' + chain;
		fire('groupBookingsSubmit', getVariables({ theaterChainName }));
	});

	Store.on('WRONG_REGION', (region, ticketType) => fire('wrongRegion', getVariables({ region, ticketType })));
	Store.on('SHOWTIMES_ERROR', (ticketType) => fire('showtimesError', getVariables({ ticketType })));
	Store.on('SHOWTIMES_EMPTY', (region, ticketType) => {
		const { countryCode = '', city = '' } = region;
		fire('showtimesEmpty', getVariables({ countryCode, city, ticketType }));
	});

	Store.on('FILTER_SELECTED_TIME', (times, ticketType = 'tickets') => fire('showtimesFilterSelectedTime', getVariables({ times, ticketType })));
	Store.on('FILTER_SELECTED_DAY', (date, ticketType = 'tickets') => fire('showtimesFilterSelectedDay', getVariables({ date, ticketType })));
	Store.on('FILTER_SELECTED_FORMAT', (formats, ticketType = 'tickets') => fire('showtimesFilterSelectedFormat', getVariables({ formats, ticketType })));

	Store.on('SET_USER_LOCATION', (_, source, ticketType) => fire('showtimesSetUserLocation', getVariables({ source, ticketType })));
	Store.on('GEO_FROM_IP', (geo, ticketType) => {
		const { countryCode = '', city = '' } = geo;
		fire('showtimesGeoFromIP', getVariables({ countryCode, city, ticketType }));
	});
	Store.on('USER_LOCATION_UPDATED', ({ geo, source, ticketType = 'tickets' }) => {
		if (source === 'landing' || !geo) {
			return;
		}
		const { city = '', countryCode = '' } = geo;
		fire('showtimesUserLocationUpdated', getVariables({ city, countryCode, ticketType }));
	});


	Store.on('CALENDAR_CLICKED', ({ label }) => fire('calendarReminder', getVariables({ calendarLabel: label })));

	/* Video Events */
	Store.on('VIDEO_PLAYER_PROGRESS', ({ platform, video, step, isShowtimesMessage, isTrailerOverlay }) => {
		const progress = step === 95 ? 100 : step;
		let type = isTrailerOverlay ? 'trailer' : 'videos';
		fire('videoProgress', getVariables({ video, platform, progress, type, showtimesMessage: isShowtimesMessage ? 'true' : 'false' }));
	});

	/* Photobooth Events */
	Store.on('START_PHOTOBOOTH', () => fire('photoboothStart'));
	Store.on('PHOTOBOOTH_GRANT_CAMERA_ACCESS', () => fire('photoboothAllowCameraAccess'));
	Store.on('PHOTOBOOTH_DENY_CAMERA_ACCESS', () => fire('photoboothDenyCameraAccess'));
	Store.on('PHOTOBOOTH_USE_CAMERA', () => fire('photoboothUseCamera'));
	Store.on('PHOTOBOOTH_UPLOAD', () => fire('photoboothUpload'));
	Store.on('PHOTOBOOTH_PHOTO_CAPTURED', () => fire('photoboothPhotoCaptured'));
	Store.on('PHOTOBOOTH_IMAGE_UPLOADED', () => fire('photoboothImageUploaded'));
	Store.on('SHARE_PHOTOBOOTH_IMAGE', () => fire ('photoboothShare'));
	Store.on('DOWNLOAD_PHOTOBOOTH_IMAGE', () => fire('photoboothDownload'));
	Store.on('PHOTOBOOTH_SOCIAL_CLICK', ({ platform }) => fire('photoboothSocialClick', getVariables({ platform })));
	Store.on('PHOTOBOOTH_RESTART', () => fire('photoboothRestart'));

	/* QuizPhotobooth Events */
	// Store.on('TRACK_QUIZPHOTOBOOTH_VIEW', (cat) => fire('view', cat, '-'));
	// Store.on('TRACK_QUIZPHOTOBOOTH_NAME_CLICK', () => fire('name-click', 'name', '-'));
	// Store.on('TRACK_QUIZPHOTOBOOTH_START_CLICK', () => fire('start-button-click', 'name', '-'));
	// Store.on('TRACK_QUIZPHOTOBOOTH_ANSWER_CLICK', ({ questionNum, answer }) => fire('answer-click', 'quiz', `question-${questionNum}_${answer}`));
	// Store.on('TRACK_QUIZPHOTOBOOTH_RESULT_REVEAL', (result) => fire('result-reveal', 'results', result));
	// Store.on('TRACK_QUIZPHOTOBOOTH_RESULT_SHARE', () => fire('result-share-cta', 'results', '-'));
	// Store.on('TRACK_QUIZPHOTOBOOTH_UPLOAD_OR_TAKE_PHOTO', (action) => fire('photo-options-click', 'results', action));
	// Store.on('TRACK_QUIZPHOTOBOOTH_CAMERA_PERMISSIONS', (allowOrDeny) => fire('camera-permissions', 'camera', allowOrDeny));
	// Store.on('TRACK_QUIZPHOTOBOOTH_CAPTURE_PHOTO', () => fire('photo-capture-click', 'camera', '-'));
	// Store.on('TRACK_QUIZPHOTOBOOTH_FLIP_CAMERA', () => fire('flip-camera', 'camera', '-'));
	// Store.on('TRACK_QUIZPHOTOBOOTH_RETAKE_OR_CONTINUE', (retakeOrContinue) => fire('resize-button-click', 'resize', retakeOrContinue));
	// Store.on('TRACK_QUIZPHOTOBOOTH_PINCH_ZOOM', () => fire('pinch-zoom', 'resize', '-'));
	// Store.on('TRACK_QUIZPHOTOBOOTH_ZOOM_CLICK', (plusOrMinus) => fire('zoom-click', 'resize', plusOrMinus));
	// Store.on('TRACK_QUIZPHOTOBOOTH_ROTATE_TWIST', () => fire('rotate-twist', 'resize', '-'));
	// Store.on('TRACK_QUIZPHOTOBOOTH_ROTATE_CLICK', (dir) => fire('rotate-click', 'resize', dir));
	// Store.on('TRACK_QUIZPHOTOBOOTH_RESULT_REVEAL_NO_INTERSTITIAL', () => fire('result-share-cta', 'shareable', '-'));
	// Store.on('TRACK_QUIZPHOTOBOOTH_CAROUSEL_SWIPE', () => fire('share-card-carousel-swipe', 'shareable', '-'));
	// Store.on('TRACK_QUIZPHOTOBOOTH_SHARE_CARD_CLICK', () => fire('share-card-click', 'shareable', '-'));
	// Store.on('TRACK_QUIZPHOTOBOOTH_REDO_CLICK', () => fire('redo-click', 'shareable', '-'));
	// Store.on('TRACK_QUIZPHOTOBOOTH_ERROR_BACK', (screen) => fire('error-screen-back', `${screen}-error`, 'back-home'));
	// Store.on('TRACK_QUIZPHOTOBOOTH_LOGO_CLICK', () => fire('logo-home', 'header', 'back-home'));

	/* Custom Event - for single app GA tracking */
	Store.on('CUSTOM_EVENT', ({ category, action, label }) => {
		fire('customEvent', getVariables({ category, action, label }));
	});

	_init = (data) => {
		if (!data) {
			return;
		}
		if (data.variables) {
			customVariables = data.variables;
		}
		Object.entries(data).forEach(([id, pixel]) => register(pixel, id));
	};

	_getStaticPixels = (data) => {
		let variables = getVariables();
		return Object.values(data).filter(pixel => shouldUsePixel(pixel) && pixel.static).map(pixel => {
			const code = withVariables(pixel.code, variables);
			return isHTML(code) ? code : `<script>${safeStringForHTMLScript(code)}</script>`;
		});
	};
	_register = register;
	_fire = fire;
	_getVariables = getVariables;
}

export function init(data) {
	return _init?.(data);
}

export function getStaticPixels(data) {
	return _getStaticPixels?.(data);
}

export function register(...args) {
	return _register?.(...args);
}

export function fire(...args) {
	return _fire?.(...args);
}

export function getVariables(...args) {
	return _getVariables?.(...args);
}